import type {
  DeathConfirmationState,
  DeliveryStatus,
  PhysicalDispatchStatus,
} from '../api/types'

// Single mapping table for every state colour in the operator UI. Each
// page imports `StatusBadge` rather than hand-rolling Tailwind classes
// so the colour language stays consistent across queues.

type Tone = 'calm' | 'neutral' | 'warning' | 'danger' | 'info'

const TONE_CLASSES: Record<Tone, string> = {
  calm:    'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200',
  neutral: 'bg-stone-100 text-stone-800 ring-1 ring-stone-200',
  warning: 'bg-amber-100 text-amber-900 ring-1 ring-amber-200',
  danger:  'bg-red-100 text-red-800 ring-1 ring-red-200',
  info:    'bg-sky-100 text-sky-800 ring-1 ring-sky-200',
}

const DEATH_CONFIRMATION_TONES:
    Record<DeathConfirmationState, Tone> = {
  ACTIVE: 'calm',
  ESCALATING: 'warning',
  GRACE_PERIOD: 'warning',
  FINAL_WARNING: 'warning',
  DELIVERING: 'info',
}

const DELIVERY_STATUS_TONES: Record<DeliveryStatus, Tone> = {
  UNSPECIFIED: 'neutral',
  PENDING: 'neutral',
  SENDING: 'info',
  SENT: 'info',
  DELIVERED: 'calm',
  FAILED_TRANSIENT: 'warning',
  FAILED_PERMANENT: 'danger',
  READ: 'calm',
  EXPIRED: 'neutral',
  REVOKED: 'neutral',
}

const PHYSICAL_DISPATCH_TONES: Record<PhysicalDispatchStatus, Tone> = {
  UNSPECIFIED: 'neutral',
  NOT_REQUESTED: 'neutral',
  PENDING: 'warning',
  DISPATCHED: 'info',
  RECEIVED: 'calm',
  FAILED: 'danger',
}

interface StatusBadgeProps {
  kind: 'death_confirmation' | 'delivery' | 'physical_dispatch'
  value: string
  className?: string
}

export function StatusBadge({ kind, value, className }: StatusBadgeProps) {
  let tone: Tone
  if (kind === 'death_confirmation') {
    tone = DEATH_CONFIRMATION_TONES[value as DeathConfirmationState]
      ?? 'neutral'
  } else if (kind === 'delivery') {
    tone = DELIVERY_STATUS_TONES[value as DeliveryStatus] ?? 'neutral'
  } else {
    tone = PHYSICAL_DISPATCH_TONES[value as PhysicalDispatchStatus]
      ?? 'neutral'
  }
  const cls = [
    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
    TONE_CLASSES[tone],
    className,
  ].filter(Boolean).join(' ')
  return (
    <span className={cls} data-status={value} data-tone={tone}>
      {value}
    </span>
  )
}
